/**
 * Motor de sugestão de resposta, caso a caso.
 *
 * O que ele é: uma tradução das 13 regras de atendimento e dos 12 modelos que a
 * equipe já usou de verdade no chat "Assistente" do Mercado Livre (auditados até
 * 04/09/2026) para uma recomendação por caso.
 *
 * O que ele NÃO é: um gerador de texto livre. Ele não lê a conversa do comprador —
 * esses textos não estão na base (o dado disponível é só a CONTAGEM de mensagens de
 * cada reclamação). Por isso toda sugestão sai com lacunas explícitas ([N], [valor])
 * para a analista completar, e nunca afirma fato que a base não tenha.
 *
 * Quando o backend expuser `/claims/{id}/messages`, trocar `sugerir()` pela chamada
 * ao Hermes mantendo esta mesma forma de retorno — ver SPEC_BACKEND_FLASK.md.
 */

import type { CasoMl, FilaShopee } from './logic'

export type Conduta = 'responder' | 'nao_responder' | 'resolver' | 'acompanhar'

export type Chance = 'baixa' | 'media' | 'indefinida'

export type Sugestao = {
  conduta: Conduta
  condutaLabel: string
  /** chance de o ML excluir a reclamação da reputação */
  chance: Chance
  chanceLabel: string
  /** por que essa conduta — sempre ancorado num fato do caso ou numa regra escrita */
  porque: string
  /** texto pronto para colar; null quando a conduta é não responder */
  texto: string | null
  /** o que não fazer neste caso, das regras auditadas */
  avisos: string[]
  /** de onde veio a recomendação */
  base: string
}

const ITEM_FALTANDO = new Set(['PDD9952', 'PDD9955'])
const PRODUTO_COM_PROBLEMA = new Set(['PDD9946', 'PDD9949', 'PDD9944', 'PDD9942', 'PDD9941'])
const ARREPENDIMENTO = new Set(['PDD9939', 'PNR9501', 'PNR9509'])
const NAO_RECEBEU = new Set(['PNR9560', 'PNR9567'])
const ENTREGA = new Set(['PNR9508', 'PNR9513', 'PNR9504', 'PNR9510', 'PNR9511', 'PNR9505'])

const AVISOS_GERAIS = [
  'Nunca peça foto ou contagem direto ao comprador — conduza o mediador a pedir.',
  'Nunca admita que não sabe o que foi enviado.',
  'Nunca culpe o Full, os Correios ou o próprio ML.',
  'Nunca acuse o comprador de má-fé, nem por insinuação.',
  'Nunca mande o comprador cancelar a reclamação.',
  'Nunca prometa reembolso por escrito antes de registrar a solução na plataforma.',
]

const CHANCE_LABEL: Record<Chance, string> = {
  baixa: 'Baixa chance de exclusão',
  media: 'Pode ser excluída',
  indefinida: 'Depende do que o comprador escreveu',
}

const CONDUTA_LABEL: Record<Conduta, string> = {
  responder: 'Responder no chat',
  nao_responder: 'Não responder',
  resolver: 'Resolver primeiro',
  acompanhar: 'Acompanhar',
}

const monta = (s: Omit<Sugestao, 'condutaLabel' | 'chanceLabel'>): Sugestao => ({
  ...s,
  condutaLabel: CONDUTA_LABEL[s.conduta],
  chanceLabel: CHANCE_LABEL[s.chance],
})

export function sugerir(caso: CasoMl): Sugestao {
  const produto = caso.itemTitulo || 'o produto deste anúncio'

  // ---- item faltando: a jogada que já resolveu um caso de R$ 473,40 por zero
  if (ITEM_FALTANDO.has(caso.motivoCod)) {
    return monta({
      conduta: 'responder',
      chance: 'baixa',
      porque:
        'Item faltando é motivo que o ML trata como válido — a reclamação não sai da reputação. ' +
        'O que está em jogo aqui é o valor do reembolso: confirmar a quantidade exata antes de aceitar ' +
        'já derrubou um caso de R$ 473,40 para zero, e outro de R$ 315,60 para R$ 26,30.',
      texto:
        `O cliente comprou [N] kits de [M] unidades cada, totalizando [N×M] peças. ` +
        `Posso enviar as unidades que estão faltando, mas preciso ter certeza de quantas exatamente ` +
        `estão faltando. Gostaria de ter o ok do cliente antes de seguir.`,
      avisos: [
        'Nunca peça foto ou contagem direto ao comprador — ofereça resolver e explique que precisa da quantidade certa para não errar o envio; o mediador então oferece a foto sozinho.',
        'Se o mediador exigir escolher uma solução, escolha de forma condicional em vez de recusar: "se forem [X] peças, o reembolso seria de R$ [valor] — nesse caso autorizo; caso contrário, prefiro enviar as peças faltantes."',
        'Nunca admita que não sabe o que foi enviado — diga a composição do anúncio ([N] tiras de [M]).',
      ],
      base: 'Regra ⭐ (item faltando) + regras 1, 2, 3 e 9 do chat do Meli',
    })
  }

  // ---- produto com defeito / diferente do anunciado
  if (PRODUTO_COM_PROBLEMA.has(caso.motivoCod)) {
    return monta({
      conduta: 'resolver',
      chance: 'baixa',
      porque:
        `"${caso.motivoLabel}" está na lista de motivos que o ML considera válidos: não existe texto que ` +
        'reverta. O caminho é resolver (reenvio ou reembolso) e tratar a causa no anúncio ou no lote — ' +
        'contestar aqui gasta tempo e não muda a reputação.',
      texto:
        `Registrei a solução aqui no Mercado Livre. ` +
        `Sobre ${produto}: [reenvio da unidade / reembolso], conforme a opção selecionada na plataforma.`,
      avisos: [
        'Nunca prometa reembolso por escrito no chat — registre a solução pela própria plataforma e diga só que registrou.',
        'Nunca diga que o comprador usou o produto.',
        'Troque "tenho certeza" por um número verificável (quantidade, lacre, composição do anúncio).',
      ],
      base: 'Lista de motivos válidos do ML + regras 7, 10 e 12',
    })
  }

  // ---- arrependimento: o silêncio é a conduta
  if (ARREPENDIMENTO.has(caso.motivoCod)) {
    return monta({
      conduta: 'nao_responder',
      chance: 'media',
      porque:
        'Arrependimento é o melhor desfecho possível para a conta e já fica registrado assim. A devolução ' +
        'corre sozinha — responder não melhora nada e pode piorar.',
      texto: null,
      avisos: [
        'Atenção: se o comprador escreveu palavras de defeito ou "não é igual à foto" sob esse motivo, o silêncio deixa de ser preferência e vira defesa — responder ali convida o ML a reclassificar para um motivo que não pode ser excluído. Nesse caso, leia a conversa antes de decidir.',
      ],
      base: 'Leitura 11 do painel (arrependimento: não responda nada)',
    })
  }

  // ---- entregue, mas diz não ter recebido
  if (NAO_RECEBEU.has(caso.motivoCod)) {
    return monta({
      conduta: 'responder',
      chance: 'indefinida',
      porque:
        'Caso de apuração de envio. A resposta serve para manter o prazo cumprido enquanto a apuração corre, ' +
        'sem afirmar nada que a Fiesta ainda não possa provar.',
      texto: 'A apuração do envio está em andamento. Assim que houver retorno, registro aqui.',
      avisos: [
        'Nunca acuse o comprador de golpe, nem por insinuação — "muitos agem de má fé" não tem versão certa.',
        'Nunca cole conversa interna (transportadora, expedição) no chat.',
      ],
      base: 'Regras 5, 6 e 11 do chat do Meli',
    })
  }

  // ---- entrega e endereço
  if (ENTREGA.has(caso.motivoCod)) {
    return monta({
      conduta: 'acompanhar',
      chance: 'indefinida',
      porque:
        'Motivo ligado à entrega. Se a Fiesta despachou dentro do prazo, há base para pedir exclusão quando ' +
        'a reclamação encerrar; se o despacho atrasou, o ML considera válida. Confirmar a data de despacho ' +
        'no envio antes de agir.',
      texto:
        'Registro que o pedido foi despachado em [data], dentro do prazo de manuseio. ' +
        'Sigo à disposição para o que a plataforma orientar.',
      avisos: [
        'Nunca culpe os Correios, o Full ou o ML — o ML lê isso como falha do vendedor.',
        'Confirme a data real de despacho antes de escrever: se atrasou, não afirme prazo cumprido.',
      ],
      base: 'Lista de motivos do ML + regra 4',
    })
  }

  // ---- demais motivos
  return monta({
    conduta: 'acompanhar',
    chance: 'indefinida',
    porque:
      `O código ${caso.motivoCod} não cai em nenhum dos grupos com conduta escrita. ` +
      'O motivo real sai das mensagens do comprador — ler a conversa antes de responder.',
    texto: null,
    avisos: AVISOS_GERAIS,
    base: 'Sem regra específica para este motivo',
  })
}

// ---------------------------------------------------------------- Shopee

/**
 * Na Shopee a pergunta não é "o que respondo", é "disputo ou não" — e a resposta sai
 * de três perguntas, nesta ordem (triagem do painel):
 *   1. o comprador já postou a devolução?  → decide se a janela de reembolso parcial existe
 *   2. o motivo joga a culpa no vendedor?  → decide se o pedido está entrando na Taxa de Não Envio
 *   3. existe evidência forte, de verdade? → decide se a disputa se sustenta
 *
 * Aqui a conversa do comprador EXISTE na base (campo `text_reason`), então a sugestão
 * pode citá-la. O que a base não diz é se há evidência — isso continua com a analista.
 */

/** Motivos que a Shopee contabiliza contra o vendedor na Taxa de Não Envio. */
const CULPA_DO_VENDEDOR = new Set(['WRONG_ITEM', 'ITEM_MISSING', 'FUNCTIONAL_DMG', 'DAMAGED_OTHERS'])

/** Situações em que o comprador ainda não despachou — janela de parcial aberta. */
const ANTES_DE_POSTAR = new Set(['REQUESTED', 'PROCESSING'])

export type SugestaoShopee = {
  conduta: 'disputar' | 'reembolso_parcial' | 'nao_disputar' | 'responder'
  condutaLabel: string
  tne: boolean
  porque: string
  texto: string | null
  avisos: string[]
  base: string
}

const CONDUTA_SHOPEE: Record<SugestaoShopee['conduta'], string> = {
  disputar: 'Disputar — com evidência',
  reembolso_parcial: 'Propor reembolso parcial',
  nao_disputar: 'Não disputar',
  responder: 'Responder',
}

export function sugerirShopee(caso: FilaShopee): SugestaoShopee {
  const tne = CULPA_DO_VENDEDOR.has(caso.motivoCod)
  const janelaParcialAberta = ANTES_DE_POSTAR.has(caso.status)
  const label = (c: SugestaoShopee['conduta']) => CONDUTA_SHOPEE[c]

  if (caso.status === 'SELLER_DISPUTE') {
    return {
      conduta: 'disputar',
      condutaLabel: label('disputar'),
      tne,
      porque:
        'A disputa já está aberta. O que decide agora é a evidência anexada dentro do prazo — sem ela, ' +
        'a disputa não tira o pedido da Taxa de Não Envio.',
      texto: null,
      avisos: ['Prazo: 3 dias pela Central do Vendedor; depois, só via chat, até 30 dias da abertura.'],
      base: 'Triagem, pergunta 3',
    }
  }

  if (tne && janelaParcialAberta) {
    return {
      conduta: 'reembolso_parcial',
      condutaLabel: label('reembolso_parcial'),
      tne: true,
      porque:
        `"${caso.motivoCod}" joga a culpa no vendedor, então este pedido está entrando na Taxa de Não Envio agora. ` +
        'E o comprador ainda não postou a devolução — é a única janela em que dá para propor pagar só a parte ' +
        'faltante, ou repor o item, em vez de perder a venda inteira.',
      texto:
        'Posso repor [o item faltante / a unidade avariada] ou reembolsar a parte correspondente, ' +
        'sem necessidade de devolver o pedido inteiro. Proponho pela Central para o cliente avaliar.',
      avisos: [
        'A proposta tem que sair pelo botão de reembolso parcial ou contraproposta da própria Central — nunca por fora da plataforma.',
        'Reembolso parcial NÃO tira o pedido da Taxa de Não Envio. Só a disputa com evidência válida tira.',
        'Se o comprador não devolver em 7 dias da aprovação, a Shopee cancela a devolução sozinha.',
      ],
      base: 'Triagem, perguntas 1 e 2 + janela 1 (reembolso parcial)',
    }
  }

  if (tne) {
    return {
      conduta: 'disputar',
      condutaLabel: label('disputar'),
      tne: true,
      porque:
        `"${caso.motivoCod}" conta contra a loja na Taxa de Não Envio, e a janela de reembolso parcial já fechou ` +
        '(o comprador postou a devolução). Só a disputa com evidência válida tira o pedido da taxa.',
      texto: null,
      avisos: [
        'Disputar só o que tem evidência de verdade — disputa sem prova gasta prazo e não muda a taxa.',
        'Prazo: 3 dias pela Central do Vendedor, 30 no total contando o chat.',
      ],
      base: 'Triagem, perguntas 1, 2 e 3',
    }
  }

  if (caso.pendente) {
    return {
      conduta: 'responder',
      condutaLabel: label('responder'),
      tne: false,
      porque:
        'O caso está esperando resposta do vendedor e o motivo não entra na Taxa de Não Envio. ' +
        'Responder aqui é manter a taxa de resposta da loja — que é uma das métricas que seguram a nota.',
      texto: caso.texto
        ? 'Recebi sua solicitação e vou seguir pela Central. [responder ao ponto que o cliente levantou]'
        : 'Recebi sua solicitação e vou seguir pela Central.',
      avisos: ['Nunca oferecer contato, pagamento ou envio por fora da plataforma.'],
      base: 'Triagem, pergunta 2 + métrica de taxa de resposta',
    }
  }

  return {
    conduta: 'nao_disputar',
    condutaLabel: label('nao_disputar'),
    tne: false,
    porque:
      'O motivo não joga a culpa no vendedor, então o pedido não entra na Taxa de Não Envio. ' +
      'Não fazer nada é a conduta certa — e os números confirmam: dois terços dos "não recebi" cancelam sozinhos.',
    texto: null,
    avisos: [],
    base: 'Triagem, pergunta 2',
  }
}

/** Prioridade na fila: o que dói primeiro. */
export function prioridade(caso: CasoMl): number {
  if (caso.temPrazo && caso.noPrazo === 0) return 0 // janela perdida
  if (caso.aberta && ITEM_FALTANDO.has(caso.motivoCod)) return 1 // dinheiro em jogo
  if (caso.aberta && PRODUTO_COM_PROBLEMA.has(caso.motivoCod)) return 2
  if (caso.aberta) return 3
  return 4
}

export const ETIQUETA_PRIORIDADE: Record<number, { label: string; tom: 'error' | 'warning' | 'default' }> = {
  0: { label: 'janela perdida', tom: 'error' },
  1: { label: 'valor em jogo', tom: 'warning' },
  2: { label: 'resolver', tom: 'warning' },
  3: { label: 'aberta', tom: 'default' },
  4: { label: 'encerrada', tom: 'default' },
}
